<template>
  <div class="chat-wrapper">
    <div :id="containerId" class="n8n-chat-container"></div>

    <!-- Image Modal -->
    <b-modal id="image-modal"
             size="xl"
             hide-footer
             centered
             @hide="closeImageModal"
             :title="selectedImage.name">
      <template #modal-header="{ close }">
        <div class="d-flex w-100 justify-content-between align-items-center">
          <h5 class="modal-title">{{ selectedImage.name }}</h5>
        </div>
        <button type="button" aria-label="Close" @click="close" class="close">×</button>
      </template>
      <template #default>
        <img :src="selectedImage.uri"
             :alt="selectedImage.alt || selectedImage.name"
             class="img-fluid w-100"
             style="max-height: 80vh; object-fit: contain;">
      </template>
    </b-modal>

    <!-- Session Replay Modal (from Rekor links) -->
    <b-modal :id="replayModalId"
             size="max"
             hide-footer
             centered
             @hide="closeSessionReplayModal">
      <template #modal-header="{ close }">
        <div class="d-flex w-100 justify-content-between align-items-center">
          <h5 class="modal-title">Session replay</h5>
        </div>
        <button type="button" aria-label="Close" @click="close" class="close">×</button>
      </template>
      <template #default>
        <session-replayer v-if="selectedSessionHash" :session-hash="selectedSessionHash" style="min-height: 705px;"></session-replayer>
      </template>
    </b-modal>
  </div>
</template>

<script>
import SessionReplayer from '@/components/insights/SessionReplayer.vue'

// Pinned on purpose: the single-flight guard hooks into the bundle's own markup
// (.chat-input, .chat-input-send-button, .chat-message-typing). An unpinned CDN bump that
// renames any of those would silently turn the guard into a no-op. Bump deliberately, and
// re-check those selectors when you do.
const N8N_CHAT_VERSION = '1.33.5';

export default {
  name: 'N8nChatWidget',
  components: { SessionReplayer },

  props: {
    // === mandatory ===
    webhookUrl: { type: String, required: true },

    // === common widget options ===
    mode: {
      type: String,
      default: 'window',
      validator: v => ['window', 'fullscreen'].includes(v)
    },
    loadPreviousSession: { type: Boolean, default: true },
    enableStreaming: { type: Boolean, default: false },
    metadata: { type: [Object, String], default: null },

    allowFileUploads: { type: Boolean, default: false },
    allowedFilesMimeTypes: { type: String, default: '' },

    initialMessages: {
      type: Array,
      default: () => [
        'Hi there! I’m Eureka, here to help with questions about Exatom, your forms, field behaviour, and metrics.'
      ]
    },
    i18n: { type: [Object, null], default: null },

    // extra headers that go in the webhook (e.g.: Authorization, etc.)
    webhookHeaders: { type: Object, default: () => ({}) },

    // Target Id (optional). If not provided, generates a unique one per instance.
    targetId: { type: String, default: 'exatom-sidebar-chat' },

    // Chat session key (optional). If defined, initializes the chat with it.
    chatSessionKey: { type: [String, null], default: null },

    // Suggestion control props
    suggestionsEnabled: { type: Boolean, default: true },
    maxSuggestions: { type: Number, default: 4 },

    // Suggestion buttons from parent component
    suggestionButtons: {
      type: Array,
      default: () => []
    }
  },

  data() {
    return {
      appInstance: null,
      messageObserver: null,
      messageTracker: null,
      dynamicActionButtons: [],
      selectedImage: {
        uri: '',
        name: '',
        alt: ''
      },
      selectedSessionHash: '',
      // Tracking deduplication
      lastTrackingTime: 0,
      trackingCooldown: 2000, // 2 seconds cooldown between events
      trackedElements: new Set(), // Track elements that already have listeners
      // Store reference to event handler for proper cleanup
      formHashChangedHandler: null,
      isMessageInFlight: false,
      singleFlightClickHandler: null,
      singleFlightKeydownHandler: null
    };
  },

  computed: {
    containerId() {
      return this.targetId || `n8n-chat-${this._uid}`;
    },
    replayModalId() {
      return `${this.containerId}-replayer`;
    },

    // Determine which buttons to use based on priority and settings
    buttonsToUse() {
      // If suggestions are disabled, return empty array
      if (!this.suggestionsEnabled) {
        return [];
      }

      let buttons = [];

      // Priority 1: Dynamic buttons from n8n responses
      if (this.dynamicActionButtons.length > 0) {
        buttons = [...this.dynamicActionButtons];
      }
      // Priority 2: Suggestion buttons from parent component prop
      else if (this.suggestionButtons.length > 0) {
        buttons = [...this.suggestionButtons];
      }

      // Apply maxSuggestions limit if specified
      if (this.maxSuggestions && buttons.length > this.maxSuggestions) {
        buttons = buttons.slice(0, this.maxSuggestions);
      }

      return buttons;
    }
  },

  mounted() {
    this.ensureCss();
    this.ensureLoader()
      .then(() => this.initChat())
      .catch(err => {
        // Silent log for debugging
        // eslint-disable-next-line no-console
        console.error('[N8nChatWidget] loader error:', err);
      });
    this.formHashChangedHandler = e => {
      this.remountChatForCurrentSession()
    }
    document.body.addEventListener('aiFormHashChanged', this.formHashChangedHandler)
  },

  beforeDestroy() {
    this.destroyChat();
    if (this.formHashChangedHandler) {
      document.body.removeEventListener('aiFormHashChanged', this.formHashChangedHandler);
      this.formHashChangedHandler = null;
    }
  },

  watch: {
    metadata: {
      handler(newMeta, oldMeta) {
        if (this.appInstance && JSON.stringify(newMeta) !== JSON.stringify(oldMeta)) {
          // Use nextTick to ensure the DOM is updated before re-initializing
          this.$nextTick(() => {
            this.remountChatForCurrentSession();
          });
        }
      },
      deep: true
    },
    // Watch for changes in suggestion-related props and re-inject the buttons.
    suggestionButtons: 'resetSuggestions',
    suggestionsEnabled: 'resetSuggestions',
    maxSuggestions: 'resetSuggestions'
  },

  methods: {
    startSingleFlight() {
      if (this.isMessageInFlight) return false;

      this.isMessageInFlight = true;

      // Let the first click/keydown reach the n8n widget before disabling its controls.
      setTimeout(() => this.applySingleFlightState(), 0);
      return true;
    },

    finishSingleFlight() {
      if (!this.isMessageInFlight) return;
      this.isMessageInFlight = false;
      this.applySingleFlightState();
    },

    applySingleFlightState() {
      const container = document.getElementById(this.containerId);
      if (!container) return;

      // The container class is the durable part of the visual state: attributes written on
      // widget-owned nodes are wiped whenever the n8n bundle re-renders, this class is not.
      // Typing stays allowed while waiting, matching the widget's own behaviour; only the
      // send paths are blocked, by the capture handlers in setupSingleFlightGuard.
      container.classList.toggle('chat-single-flight', this.isMessageInFlight);

      container.querySelectorAll('.chat-input-send-button').forEach(button => {
        button.setAttribute('aria-disabled', String(this.isMessageInFlight));
      });

      // These buttons are injected by us, so disabling them directly is safe.
      container.querySelectorAll('.action-button, .chat-retry-btn').forEach(button => {
        button.disabled = this.isMessageInFlight;
      });
    },

    setupSingleFlightGuard() {
      const container = document.getElementById(this.containerId);
      if (!container || this.singleFlightClickHandler || this.singleFlightKeydownHandler) return;

      const blockEvent = event => {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
      };

      this.singleFlightClickHandler = event => {
        const sendButton = event.target.closest?.('.chat-input-send-button');
        if (!sendButton || sendButton.disabled) return;

        if (this.isMessageInFlight) {
          blockEvent(event);
          return;
        }

        this.startSingleFlight();
      };

      this.singleFlightKeydownHandler = event => {
        const input = event.target.closest?.('.chat-input textarea, .chat-input input[type="text"]');
        if (!input || event.key !== 'Enter' || event.shiftKey || event.isComposing) return;

        if (this.isMessageInFlight) {
          blockEvent(event);
          return;
        }

        if (String(input.value || '').trim()) {
          this.startSingleFlight();
        }
      };

      container.addEventListener('click', this.singleFlightClickHandler, true);
      container.addEventListener('keydown', this.singleFlightKeydownHandler, true);
      this.applySingleFlightState();
    },

    clearInjectedActionButtons() {
      const container = document.getElementById(this.containerId);
      if (!container) return;
      const existingButtons = container.querySelector('.chat-action-buttons');
      if (existingButtons) existingButtons.remove();
    },

    resetSuggestions() {
      this.dynamicActionButtons = [];
      this.clearInjectedActionButtons();
      try { this.injectActionButtons(); } catch (e) {}
    },
    ensureCss() {
      const cssId = 'n8n-chat-style';
      if (!document.getElementById(cssId)) {
        const link = document.createElement('link');
        link.id = cssId;
        link.rel = 'stylesheet';
        // official CSS path on CDN, pinned: see N8N_CHAT_VERSION
        link.href = `https://cdn.jsdelivr.net/npm/@n8n/chat@${N8N_CHAT_VERSION}/dist/style.css`;
        document.head.appendChild(link);
      }
      // if using fullscreen, ensure the container has height/width:
      if (this.mode === 'fullscreen') {
        const el = document.getElementById(this.containerId);
        if (el) {
          el.style.width = '100%';
          el.style.height = '100%';
        }
      }
    },

    ensureLoader() {
      // If the function already exists, we are ready
      if (window.__n8nCreateChat) return Promise.resolve();

      // creates a <script type="module"> with import from CDN
      return new Promise((resolve, reject) => {
        const id = 'n8n-chat-loader-cdn';
        if (document.getElementById(id)) return resolve();

        // Expose createChat on window
        const code = `
          import { createChat } from 'https://cdn.jsdelivr.net/npm/@n8n/chat@${N8N_CHAT_VERSION}/dist/chat.bundle.es.js';
          window.__n8nCreateChat = createChat;
        `;
        const blob = new Blob([code], { type: 'text/javascript' });
        const url = URL.createObjectURL(blob);

        const s = document.createElement('script');
        s.id = id;
        s.type = 'module';
        s.src = url;
        s.onload = () => { URL.revokeObjectURL(url); resolve(); };
        s.onerror = (e) => {
          URL.revokeObjectURL(url);
          reject(e);
        };
        document.head.appendChild(s);
      });
    },

    initChat() {
      const start = () => {
        if (!window.__n8nCreateChat) {
          // try again in the next event loop turn
          return void setTimeout(start, 0);
        }
        // avoid instantiating more than once
        if (this.appInstance) return;

        const targetSelector = `#${this.containerId}`;
        const opts = {
          target: targetSelector,
          webhookUrl: this.webhookUrl,
          mode: this.mode,
          loadPreviousSession: this.loadPreviousSession,
          enableStreaming: this.enableStreaming,
          metadata: this.metadata,
          allowFileUploads: this.allowFileUploads,
          initialMessages: this.initialMessages,
          i18n: this.i18n,
          webhookConfig: { headers: this.webhookHeaders }
        };

        // Only set allowedFilesMimeTypes when file uploads are enabled
        if (this.allowFileUploads) {
          opts.allowedFilesMimeTypes = this.allowedFilesMimeTypes;
        }

        // Add chatSessionKey if provided
        if (this.chatSessionKey) opts.chatSessionKey = this.chatSessionKey;

        try {
          this.appInstance = window.__n8nCreateChat(opts);
          this.setupSingleFlightGuard();

          // Wait for the chat to be fully rendered, then inject action buttons and setup image handlers
          this.$nextTick(() => {
            setTimeout(() => {
              this.injectActionButtons();
              this.setupImageClickHandlers(); // Initial setup for existing images
              this.setupLinkClickHandlers();  // Initial setup for specific links
            }, 500); // Give time for N8N chat to fully render
          });

          // Setup message observation for dynamic action buttons
          this.setupMessageObserver();

          // Setup separate message tracker for user message tracking
          this.setupMessageTracker();

          // emit an event so the page can react (optional)
          this.$emit('ready', this.appInstance);
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error('[N8nChatWidget] init error:', e);
        }
      };
      start();
    },

    // Internal remounts keep the same n8n sessionId, so they must also keep any running lock.
    remountChatForCurrentSession() {
      this.destroyChat({ preserveSingleFlight: true });
      this.initChat();
    },

    destroyChat({ preserveSingleFlight = false } = {}) {
      if (!preserveSingleFlight) this.finishSingleFlight();

      const container = document.getElementById(this.containerId);
      if (container && this.singleFlightClickHandler) {
        container.removeEventListener('click', this.singleFlightClickHandler, true);
      }
      if (container && this.singleFlightKeydownHandler) {
        container.removeEventListener('keydown', this.singleFlightKeydownHandler, true);
      }
      this.singleFlightClickHandler = null;
      this.singleFlightKeydownHandler = null;

      // Disconnect message observer
      if (this.messageObserver) {
        this.messageObserver.disconnect();
        this.messageObserver = null;
      }

      // Clean up message tracker
      if (this.messageTracker) {
        clearInterval(this.messageTracker);
        this.messageTracker = null;
      }

      // Reset tracking deduplication state
      this.trackedElements.clear();
      this.lastTrackingTime = 0;

      // Reset dynamic suggestions and remove injected buttons
      this.dynamicActionButtons = [];
      this.clearInjectedActionButtons();

      // Reset modal state
      this.selectedSessionHash = '';

      // try to cleanly unmount (in case the object returns Vue 3 compatible API)
      if (this.appInstance && typeof this.appInstance.unmount === 'function') {
        try { this.appInstance.unmount(); } catch (e) {}
      }
      this.appInstance = null;

      // clean the container (avoid duplicating UI on remount)
      const el = document.getElementById(this.containerId);
      if (el) el.innerHTML = '';
    },

    setupMessageObserver() {
      const container = document.getElementById(this.containerId);
      if (!container) {
        return;
      }

      // Observe assistant messages that are not loading
      this.messageObserver = new MutationObserver((records) => {
        for (const m of records) {
          for (const node of m.addedNodes) {
            if (node.nodeType !== Node.ELEMENT_NODE) continue;
            // Ignore mutations caused by our own injected UI to avoid loops/flicker
            if (node.closest?.('.chat-feedback-row, .chat-feedback, .chat-action-buttons')) continue;
            const root = node.closest?.('.chat-message') || node;
            if (!this.isAssistantMessage(root) || this.isLoadingMessage(root)) continue;

            this.finishSingleFlight();

            // Give a brief tick for inner HTML to settle, then parse/update
            setTimeout(() => {
              this.parseMessageResponse(root);
              try { this.updateFeedbackForLastAssistant(root); } catch (e) {}
              // Ensure any new images and links get handlers
              this.setupImageClickHandlers();
              this.setupLinkClickHandlers();
            }, 100);
          }
        }
      });

      this.messageObserver.observe(container, { childList: true, subtree: true });
    },

    setupMessageTracker() {
      const container = document.getElementById(this.containerId);
      if (!container) return;

      // Message tracker running
      const observer = new MutationObserver((mutations) => {
        mutations.forEach(mutation => {
          if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
            mutation.addedNodes.forEach(node => {
              const root = node.closest?.('.chat-message') || node;
              // Check if the message is from the assistant and has content
              if (this.isAssistantMessage(root)) {
                const now = Date.now();
                if (now - this.lastTrackingTime > this.trackingCooldown) {
                  this.lastTrackingTime = now;
                  this.$root.analyticsEvent('Ai Chat - message received');
                }
              }
            });
          }
        });
      });
      observer.observe(container, { childList: true, subtree: true });
    },

    parseMessageResponse(messageElement) {
      const root = messageElement.closest?.('.chat-message') || messageElement;

      // Mark the first assistant message as initial greeting (to never show feedback)
      this.markInitialGreeting();

      // Detect and handle error payloads like {"error": true}
      if (this.handleAssistantError(root)) {
        return;
      }

      // DOM-first only: look for <code class="language-suggestions">...</code>
      const codeEl = root.querySelector('code.language-suggestions, pre code.language-suggestions');
      if (!codeEl || !codeEl.textContent) return;

      try {
        const arr = JSON.parse(codeEl.textContent.trim());
        if (!Array.isArray(arr)) return;

        // Set dynamic buttons and remove the code block from the DOM
        this.dynamicActionButtons = arr;
        const removable = codeEl.closest('pre') || codeEl;
        removable?.parentElement?.removeChild(removable);

        // Re-inject buttons and update feedback after DOM settles
        this.$nextTick(() => {
          setTimeout(() => {
            this.injectActionButtons();
            this.updateFeedbackForLastAssistant(root);
          }, 100);
        });
      } catch (e) {
        // Silent fail on JSON parse errors
      }
    },

    // Try to detect error responses rendered by the chat as JSON and transform UX accordingly
    handleAssistantError(root) {
      try {
        if (!root || root.getAttribute('data-error-handled') === 'true') return false;

        // Helper to finalize handling: replace text, reset suggestions, track event
        const applyErrorUx = () => {
          const host = this.getMessageTextHost(root) || root;
          // Replace any existing content with the friendly error message
          if (host) {
            host.textContent = 'Ops, something got wrong, try again please';
          }
          // Mark to avoid re-handling the same message
          root.setAttribute('data-error-handled', 'true');

          // Ensure any feedback controls are removed for error messages
          try {
            root.querySelectorAll('.chat-feedback-row, .chat-feedback').forEach(node => node.remove());
          } catch (e) {}

          // Reset buttons to page defaults
          try { this.resetSuggestions(); } catch (e) {}

          this.$root.analyticsEvent('Ai Chat - error');

          this.injectRetryButton(root);
        };

        // parse the visible text content as JSON
        const host = this.getMessageTextHost(root) || root;
        const textContent = (host?.textContent || '').trim();

        if (textContent) {
          // Check for specific error text messages
          if (textContent === 'Error in workflow' || textContent === 'Ops, something got wrong, try again please' || textContent.includes('TEST_ERROR')) {
            applyErrorUx();
            return true;
          }

          try {
            const obj = JSON.parse(textContent);
            if (obj && obj.error === true) {
              applyErrorUx();
              return true;
            }
          } catch (_) {/* not pure JSON text */}
        }

        return false;
      } catch (e) {
        // On any unexpected error, do not block normal processing
        return false;
      }
    },

    injectRetryButton(messageElement) {
      try {

        // Delay injection to ensure n8n chat has finished rendering
        setTimeout(() => {
          const feedbackRow = document.createElement('div');
          feedbackRow.className = 'chat-feedback-row';

          const retryBtn = document.createElement('button');
          retryBtn.className = 'chat-feedback-btn chat-retry-btn';
          retryBtn.type = 'button';
          retryBtn.innerHTML = '<i class="material-icons" style="font-size: 14px; margin-right: 4px;">refresh</i> Retry';
          retryBtn.disabled = this.isMessageInFlight;

          retryBtn.onclick = (e) => {
            e.preventDefault();
            e.stopPropagation();
            if (this.isMessageInFlight) return;

            // 1. Find the last user message (the one that caused this error)
            // The structure is usually: ... -> User Message -> Assistant Message (Error)
            // We need to traverse up/previous to find the last .chat-message-from-user
            const container = document.getElementById(this.containerId);
            if (!container) return;

            const allMessages = Array.from(container.querySelectorAll('.chat-message'));
            const errorMsgIndex = allMessages.indexOf(messageElement.closest('.chat-message') || messageElement);

            let userMessageEl = null;
            // Search backwards from the error message
            for (let i = errorMsgIndex - 1; i >= 0; i--) {
              const msg = allMessages[i];
              if (msg.classList.contains('chat-message-from-user') || msg.classList.contains('from-user')) {
                userMessageEl = msg;
                break;
              }
            }

            if (userMessageEl) {
              // 2. Extract text
              const host = this.getMessageTextHost(userMessageEl);
              const lastPrompt = (host?.textContent || '').trim();

              if (lastPrompt) {
                // 3. Remove both messages from view
                userMessageEl.remove();
                (messageElement.closest('.chat-message') || messageElement).remove();

                // 4. Resend prompt
                this.insertPrompt(lastPrompt);
                // Trigger send
                setTimeout(() => this.submitChat(), 100);

                // 5. Track event
                this.$root.analyticsEvent('Ai Chat - retry');
              }
            }
          };

          feedbackRow.appendChild(retryBtn);

          const textHost = this.getMessageTextHost(messageElement);

          if (textHost && textHost.parentElement) {
            if (textHost.nextSibling) {
              textHost.parentElement.insertBefore(feedbackRow, textHost.nextSibling);
            } else {
              textHost.parentElement.appendChild(feedbackRow);
            }
          } else {
            messageElement.appendChild(feedbackRow);
          }
        }, 300); // Wait 300ms for n8n chat to finish rendering

      } catch (e) {
        console.warn('[N8nChatWidget] Failed to inject retry button:', e);
      }
    },

    submitChat() {
      if (this.isMessageInFlight) return;

      const container = document.getElementById(this.containerId);
      if (!container) return;

      // Try to find a send button
      const sendBtnSelectors = [
        'button[type="submit"]',
        '.chat-input button',
        '.n8n-chat-input button',
        '[data-testid="chat-send-button"]',
        '.chat-send-button',
        'button[aria-label="Send"]'
      ];

      let sendBtn = null;
      for (const selector of sendBtnSelectors) {
        sendBtn = container.querySelector(selector);
        if (sendBtn) break;
      }

      if (sendBtn) {
        sendBtn.click();
      } else {
        // Fallback: try to simulate Enter key on input
        const inputSelectors = [
          'input[type="text"]',
          'textarea',
          '[contenteditable="true"]'
        ];
        let inputElement = null;
        for (const selector of inputSelectors) {
          inputElement = container.querySelector(selector);
          if (inputElement) break;
        }

        if (inputElement) {
          inputElement.dispatchEvent(new KeyboardEvent('keydown', { key: 'Enter', code: 'Enter', bubbles: true }));
          inputElement.dispatchEvent(new KeyboardEvent('keypress', { key: 'Enter', code: 'Enter', bubbles: true }));
          inputElement.dispatchEvent(new KeyboardEvent('keyup', { key: 'Enter', code: 'Enter', bubbles: true }));
        }
      }
    },

    // Determine if the element is an assistant/bot message (and not a user or loading placeholder)
    isAssistantMessage(el) {
      if (!el) return false;
      const root = el.closest?.('.chat-message') || el;
      const cl = (root.className || '').toLowerCase();
      // Prefer explicit classes from n8n/chat
      if (cl.includes('chat-message-from-user') || cl.includes('from-user')) return false;
      const isBot = cl.includes('chat-message-from-bot') || cl.includes('from-bot') || cl.includes('assistant') || cl.includes('bot') || cl.includes('ai');
      if (!isBot) return false;
      // Exclude loading/typing placeholders
      return !this.isLoadingMessage(root);
    },

    isLoadingMessage(root) {
      const loadingSel = '.chat-message-typing, .typing, .is-typing, .loading, [data-test-id*="typing"], [data-testid*="typing"], [data-testid*="loading"], .spinner, .lds, .dots';
      return !!root.matches?.(loadingSel) || !!root.querySelector?.(loadingSel);
    },

    // Helper to get the main text/content host of a message
    getMessageTextHost(root) {
      return (
        root.querySelector('.chat-message-markdown') ||
        root.querySelector('.message-text, .chat-message-text, .message-content, .text-content, .n8n-message-text, .message-body, .content, .text, .response-text, .bot-text, .assistant-text, [class*="message"][class*="text"]') ||
        root
      );
    },

    // Collect assistant messages with actual content (filter out loading/empty)
    getAssistantMessages(container) {
      const nodes = Array.from(container.querySelectorAll('.chat-message'));
      return nodes
        .filter((n) => this.isAssistantMessage(n))
        .filter((n) => {
          const host = this.getMessageTextHost(n);
          const text = (host?.textContent || '').trim();
          return text.length > 0;
        });
    },

    // Mark the initial greeting message to never show feedback
    markInitialGreeting() {
      try {
        const container = document.getElementById(this.containerId);
        if (!container) return;

        const assistantMessages = this.getAssistantMessages(container);
        if (assistantMessages.length > 0) {
          const firstMsg = assistantMessages[0];
          // Mark it if not already marked
          if (!firstMsg.getAttribute('data-initial-greeting')) {
            firstMsg.setAttribute('data-initial-greeting', 'true');
          }
        }
      } catch (e) {
        // Silent fail
      }
    },

    // Add thumbs up/down feedback controls only to the latest assistant message
    updateFeedbackForLastAssistant(messageElement) {
      try {
        if (!messageElement) return;
        const container = document.getElementById(this.containerId);
        if (!container) return;

        const candidates = this.getAssistantMessages(container);
        if (!candidates.length) return; // Nothing to do
        const lastAssistant = candidates[candidates.length - 1];

        // Remove feedback from all previous assistant messages
        candidates.slice(0, -1).forEach(el => {
          el.querySelectorAll('.chat-feedback-row, .chat-feedback').forEach(node => node.remove());
        });

        // If the last assistant message is an error, do not add feedback controls
        if (lastAssistant.getAttribute('data-error-handled') === 'true') {
          lastAssistant.querySelectorAll('.chat-feedback-row, .chat-feedback').forEach(node => node.remove());
          return;
        }

        // Skip feedback for messages marked as initial greeting
        if (lastAssistant.getAttribute('data-initial-greeting') === 'true') {
          lastAssistant.querySelectorAll('.chat-feedback-row, .chat-feedback').forEach(node => node.remove());
          return;
        }

        // Prepare a fresh feedback row for the last assistant message
        // First, cleanup any existing to avoid duplicates
        lastAssistant.querySelectorAll('.chat-feedback-row, .chat-feedback').forEach(node => node.remove());

        const feedbackRow = document.createElement('div');
        feedbackRow.className = 'chat-feedback-row';

        const feedback = document.createElement('div');
        feedback.className = 'chat-feedback';
        feedback.setAttribute('role', 'group');
        feedback.setAttribute('aria-label', 'Provide feedback');

        const upBtn = document.createElement('button');
        upBtn.className = 'chat-feedback-btn thumbs-up';
        upBtn.type = 'button';
        upBtn.setAttribute('aria-label', 'Mark response as helpful');
        upBtn.title = 'Helpful';
        upBtn.innerHTML = '<i class="material-icons">thumb_up</i>';

        const downBtn = document.createElement('button');
        downBtn.className = 'chat-feedback-btn thumbs-down';
        downBtn.type = 'button';
        downBtn.setAttribute('aria-label', 'Mark response as not helpful');
        downBtn.title = 'Not helpful';
        downBtn.innerHTML = '<i class="material-icons">thumb_down</i>';

        const disableVoting = (type) => {
          lastAssistant.setAttribute('data-feedback-voted', type);
          upBtn.disabled = true;
          downBtn.disabled = true;
          feedback.classList.add('voted');
        };

        const sendEvent = (type) => {
          const eventName = type === 'up' ? 'Ai Chat - good response' : 'Ai Chat - bad response';
          this.$root.analyticsEvent(eventName);
        };

        upBtn.addEventListener('click', (e) => {
          e.preventDefault(); e.stopPropagation();
          if (lastAssistant.getAttribute('data-feedback-voted')) return;
          sendEvent('up');
          disableVoting('up');
        });

        downBtn.addEventListener('click', (e) => {
          e.preventDefault(); e.stopPropagation();
          if (lastAssistant.getAttribute('data-feedback-voted')) return;
          sendEvent('down');
          disableVoting('down');
        });

        feedback.appendChild(upBtn);
        feedback.appendChild(downBtn);
        feedbackRow.appendChild(feedback);

        // Insert on a new line right after the message text/content
        const textHost = this.getMessageTextHost(lastAssistant);
        if (textHost && textHost.parentElement) {
          if (textHost.nextSibling) {
            textHost.parentElement.insertBefore(feedbackRow, textHost.nextSibling);
          } else {
            textHost.parentElement.appendChild(feedbackRow);
          }
        } else {
          lastAssistant.appendChild(feedbackRow);
        }

        // If already voted, disable immediately
        const voted = lastAssistant.getAttribute('data-feedback-voted');
        if (voted) {
          upBtn.disabled = true; downBtn.disabled = true; feedback.classList.add('voted');
        }
      } catch (e) {
        console.warn('[N8nChatWidget] Failed to inject feedback controls:', e);
      }
    },

    injectActionButtons() {
      // Use the computed property to determine which buttons to show
      const buttonsToUse = this.buttonsToUse;

      if (!buttonsToUse.length) return;

      const container = document.getElementById(this.containerId);
      if (!container) return;

      // Find anchor: prefer the footer if present
      const chatFooter = container.querySelector('.chat-footer, [data-testid="chat-footer"], .n8n-chat-footer');

      // Remove existing action buttons if any
      const existingButtons = container.querySelector('.chat-action-buttons');
      if (existingButtons) {
        existingButtons.remove();
      }

      // Create action buttons container
      const actionButtonsContainer = document.createElement('div');
      actionButtonsContainer.className = 'chat-action-buttons';

      const buttonsWrapper = document.createElement('div');
      buttonsWrapper.className = 'action-buttons-container';

      // Create individual buttons
      buttonsToUse.forEach((action, index) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'action-button';
        button.textContent = action.label;
        button.title = action.description || action.label;
        button.disabled = this.isMessageInFlight;
        // Mark special actionable suggestions (action === true) with an extra class for styling
        if (action && action.action === true) {
          button.classList.add('action-button-special');
          // Accessibility hint
          button.setAttribute('aria-label', (button.getAttribute('aria-label') || action.label) + ' (special action)');
        }
        button.addEventListener('click', event => {
          event.preventDefault();
          event.stopPropagation();
          if (this.isMessageInFlight) return;
          this.$root.analyticsEvent('Ai Chat - suggestion click');

          this.insertPrompt(action.prompt);
        });
        buttonsWrapper.appendChild(button);
      });

      actionButtonsContainer.appendChild(buttonsWrapper);

      // Insert before footer if available; otherwise append to the container
      if (chatFooter && chatFooter.parentElement) {
        chatFooter.parentElement.insertBefore(actionButtonsContainer, chatFooter);
      } else {
        container.appendChild(actionButtonsContainer);
      }
    },

    pulseSuggestionButtons() {
      const container = document.getElementById(this.containerId);
      if (!container) return false;

      const buttons = Array.from(container.querySelectorAll('.chat-action-buttons .action-button'));
      if (!buttons.length) return false;

      buttons.forEach((button, index) => {
        button.classList.remove('action-button-suggestion-pulse');
        requestAnimationFrame(() => {
          setTimeout(() => {
            button.classList.add('action-button-suggestion-pulse');
          }, index * 55);
        });
      });

      setTimeout(() => {
        buttons.forEach(button => button.classList.remove('action-button-suggestion-pulse'));
      }, 1450);

      return true;
    },

    insertPrompt(prompt) {
      if (this.isMessageInFlight) return;

      // Try to find the chat input field and insert the prompt
      const container = document.getElementById(this.containerId);
      if (!container) return;

      // Look for various possible input selectors
      const inputSelectors = [
        'input[type="text"]',
        'textarea',
        '[contenteditable="true"]',
        '.chat-input input',
        '.chat-input textarea',
        '.n8n-chat-input',
        '[data-testid="chat-input"]'
      ];

      let inputElement = null;
      for (const selector of inputSelectors) {
        inputElement = container.querySelector(selector);
        if (inputElement) break;
      }

      if (inputElement) {
        // Handle different input types
        if (inputElement.tagName === 'INPUT' || inputElement.tagName === 'TEXTAREA') {
          inputElement.value = prompt;
          inputElement.focus();
          // Trigger input events to notify the chat widget
          inputElement.dispatchEvent(new Event('input', { bubbles: true }));
          inputElement.dispatchEvent(new Event('change', { bubbles: true }));
        } else if (inputElement.contentEditable === 'true') {
          inputElement.textContent = prompt;
          inputElement.focus();
          // Trigger input events for contenteditable
          inputElement.dispatchEvent(new Event('input', { bubbles: true }));
        }
      } else {
        // Fallback: copy to clipboard and notify user
        navigator.clipboard.writeText(prompt)
          .then(() => { this.$emit('prompt-copied', prompt); })
          .catch(() => { /* noop */ });
      }
    },

    // Image modal methods adapted from AnalysisDetails
    openImageModal(uri, name = 'Chat Image', alt = '') {
      this.selectedImage = {
        uri,
        name,
        alt
      };
      this.$bvModal.show('image-modal');
    },

    closeImageModal() {
      this.selectedImage = {
        uri: '',
        name: '',
        alt: ''
      };
    },

    // Setup image click handlers for chat images
    setupImageClickHandlers() {
      const container = document.getElementById(this.containerId);
      if (!container) return;

      // Find all images in the chat and make them clickable
      const images = container.querySelectorAll('.chat-message img');
      images.forEach(img => {
        // Only add click handler if not already added
        if (!img.dataset.clickHandlerAdded) {
          img.style.cursor = 'pointer';
          img.title = 'Click to expand image';

          img.onclick = (event) => {
            event.preventDefault();
            event.stopPropagation();

            const uri = img.src;
            const alt = img.alt || '';
            const name = alt || 'Chat Image';

            this.openImageModal(uri, name, alt);
          };

          // Mark as handled to avoid duplicate handlers
          img.dataset.clickHandlerAdded = 'true';
        }
      });
    },
    // Setup link click handlers for specific links (e.g., Rekor session replay)
    setupLinkClickHandlers() {
      const container = document.getElementById(this.containerId);
      if (!container) return;

      const anchors = container.querySelectorAll('.chat-message a[href]');
      anchors.forEach(a => {
        if (a.dataset.linkHandlerAdded === 'true') return;
        let href;
        try { href = new URL(a.getAttribute('href'), window.location.origin); } catch (_) { a.dataset.linkHandlerAdded = 'true'; return; }

        // Detect Rekor replay links: {VUE_APP_REKOR_API}/replay?session={clientCode}/{sessionHash}
        let rekorHost = null;
        try { rekorHost = new URL(process.env.VUE_APP_REKOR_API).hostname; } catch (_) {}
        const isRekor = !!rekorHost && href.hostname === rekorHost && href.pathname.replace(/\/+$/,'') === '/replay' && href.searchParams.has('session');
        if (!isRekor) {
          a.dataset.linkHandlerAdded = 'true';
          return;
        }

        a.addEventListener('click', (e) => {
          try {
            e.preventDefault();
            e.stopPropagation();
            const sessionParam = href.searchParams.get('session') || '';
            const parts = sessionParam.split('/');
            const sessionHash = parts[parts.length - 1];
            if (sessionHash) {
              this.openSessionReplayModal(sessionHash);
            } else {
              window.open(href.toString(), '_blank', 'noopener');
            }
          } catch (_) {
            window.open(href.toString(), '_blank', 'noopener');
          }
        });
        a.title = a.title || 'Open in modal';
        a.dataset.linkHandlerAdded = 'true';
      });
    },
    openSessionReplayModal(sessionHash) {
      this.selectedSessionHash = sessionHash;
      this.$bvModal.show(this.replayModalId);
      this.$root.analyticsEvent('Ai Chat - open replay');
    },
    closeSessionReplayModal() {
      this.selectedSessionHash = '';
    }
  }
};
</script>

<style scoped>

/* Hide chat header completely */
.n8n-chat-container :deep(.chat-header) {
  display: none !important;
}

/* Hide the default header with "Hi there! 👋" text and #101330 background */
.n8n-chat-container :deep([style*="#101330"]),
.n8n-chat-container :deep(.chat-header),
.n8n-chat-container :deep(.header),
.n8n-chat-container :deep([data-testid="chat-header"]),
.n8n-chat-container :deep(.n8n-chat-header) {
  display: none !important;
}

/* Ensure chat content fills the space */
.n8n-chat-container :deep(.chat-content),
.n8n-chat-container :deep(.chat-body),
.n8n-chat-container :deep(.n8n-chat-content) {
  height: 100% !important;
  padding-top: 0 !important;
  margin-top: 0 !important;
}

/* Action buttons styling */
.chat-wrapper {
  position: relative;
  height: 100%;
}

:deep(.chat-single-flight .chat-input-send-button),
:deep(.chat-single-flight .action-button),
:deep(.chat-single-flight .chat-retry-btn) {
  pointer-events: none;
  cursor: not-allowed !important;
  opacity: .6;
}

/* Style the injected action buttons */
:deep(.chat-action-buttons) {
  padding: 8px 12px;
  background: rgba(255, 255, 255, 0.95);
  border-top: 1px solid #e0e0e0;
  border-bottom: 1px solid #e0e0e0;
}

:deep(.action-buttons-container) {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  justify-content: flex-start;
  max-height: 80px;
  overflow-y: auto;
}

:deep(.action-button) {
  background: var(--exatom-white) !important;
  border: 1px solid var(--exatom-grey);
  border-radius: 16px;
  padding: 4px 12px;
  font-size: 12px;
  color: #495057;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  max-width: 425px;
  overflow: hidden;
  text-overflow: ellipsis;
  box-shadow: 0px 2px 29.46px 0.54px rgba(73, 73, 73, 0.1);
}

:deep(.action-button:hover) {
  border-color: #48d8c0 !important;
  box-shadow: 0px 2px 29.46px 0.54px rgba(73, 73, 73, 0.1) !important;
  transform: none !important;
}

:deep(.action-button.action-button-suggestion-pulse) {
  animation: suggestionButtonPulse 1.15s ease-out both;
}

@keyframes suggestionButtonPulse {
  0% {
    border-color: var(--exatom-grey);
    box-shadow: 0px 2px 29.46px 0.54px rgba(73, 73, 73, 0.1);
  }

  24% {
    border-color: #8b5cf6;
    box-shadow: 0 0 0 .18rem rgba(139, 92, 246, .18), 0 0 1.1rem rgba(72, 216, 192, .2);
  }

  64% {
    border-color: #a78bfa;
    box-shadow: 0 0 0 .08rem rgba(167, 139, 250, .14), 0 0 .7rem rgba(72, 216, 192, .12);
  }

  100% {
    border-color: var(--exatom-grey);
    box-shadow: 0px 2px 29.46px 0.54px rgba(73, 73, 73, 0.1);
  }
}

@media (prefers-reduced-motion: reduce) {
  :deep(.action-button.action-button-suggestion-pulse) {
    animation: none;
    border-color: #8b5cf6;
    box-shadow: 0 0 0 .12rem rgba(139, 92, 246, .16);
  }
}

/* Special action buttons (action === true) */
:deep(.action-button-special) {
  background: var(--exatom-green) !important;
  font-weight: bold;
}

/* Enhanced clickable images styling */
.n8n-chat-container :deep(.chat-message img),
.n8n-chat-container :deep(.markdown-body img),
.n8n-chat-container :deep([class*="markdown"] img) {
  max-width: 100% !important;
  height: auto !important;
  max-height: 400px !important;
  object-fit: contain !important;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 2px solid transparent;
}

.n8n-chat-container :deep(.chat-message img:hover) {
  transform: scale(1.02);
  border-color: #2563eb;
  box-shadow: 0 4px 12px rgba(37, 99, 235, 0.2);
}

.n8n-chat-container :deep(.message-content),
.n8n-chat-container :deep(.chat-message-content) {
  max-width: 100% !important;
  overflow: hidden !important;
}

.n8n-chat-container :deep(.chat-message),
.n8n-chat-container :deep(.message),
.n8n-chat-container :deep(.message-content),
.n8n-chat-container :deep(.markdown-body) {
  font-size: 12px !important;
  line-height: 1.4 !important;
}

/* Feedback controls */
:deep(.chat-feedback-row) {
  width: 100%;
  display: block;
  margin-top: 6px;
}

:deep(.chat-feedback) {
  display: inline-flex;
  gap: 6px;
  margin-top: 2px;
  opacity: 0.75;
}

:deep(.chat-feedback-btn) {
  background: transparent;
  border: 1px solid #e0e0e0;
  border-radius: 14px;
  padding: 2px 6px;
  cursor: pointer;
  color: #6b7280;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all .15s ease-in-out;
}

:deep(.chat-feedback-btn .material-icons) {
  font-size: 14px; /* smaller icons */
}

:deep(.chat-feedback-btn:hover) {
  background: #f3f4f6;
  color: #374151;
}

:deep(.chat-feedback-btn:disabled) {
  opacity: 0.5;
  cursor: default;
}

:deep(.chat-feedback.voted) {
  opacity: 0.5;
}

:deep(.chat-retry-btn) {
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
  width: auto;
  padding: 4px 12px;
  border-radius: 16px;
  background: #fff;
  border: 1px solid #e0e0e0;
  font-size: 12px;
  font-weight: 500;
  color: #4b5563;
  margin-top: 4px;
  cursor: pointer;
  line-height: 1;
  transition: all .15s ease-in-out;
}

:deep(.chat-retry-btn:hover) {
  background: #f3f4f6;
  color: #111827;
  border-color: #d1d5db;
}

:deep(.chat-retry-btn .material-icons) {
  font-size: 14px;
}

:deep(.chat-message-markdown h1) {
  font-size: 1.9rem;
  font-weight: 700;
  line-height: 1.1;
}

:deep(.chat-message-markdown h2) {
  font-size: 1.45rem;
  font-weight: 700;
  line-height: 1.15;
}

:deep(.chat-message-markdown h3) {
  font-size: 1.3rem;
  font-weight: 700;
  line-height: 1.2;
}

:deep(.chat-message-markdown h4) {
  font-size: 1.15rem;
  font-weight: 700;
  line-height: 1.3;
}

:deep(.chat-message-markdown h5), :deep(.chat-message-markdown h6) {
  font-size: 1rem;
  font-weight: 700;
  line-height: 1.4;
}
:deep(.chat-message-markdown strong), :deep(.chat-message-markdown th) {
  font-weight: 600 !important;
}
</style>
